import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "tabButton", "tabPanel",
    // Tab 1: Search by Model
    "regionSelect", "makeSelect", "modelSelect", "yearSelect",
    "modelResults", "modelLoading",
    // Tab 2: Search by Tire
    "tireSw", "tireAr", "tireRd", "tireResults", "tireLoading",
    // Tab 3: Search by Rim
    "rimBp", "rimRd", "rimRw", "rimResults", "rimLoading",
    // Raw response
    "rawEndpoint", "rawStatus", "rawTimestamp", "rawBody", "rawPanel"
  ]

  static values = {
    queryUrl: String,
    csrfToken: String
  }

  connect() {
    this.activeTab = "model"
    this.tireLoaded = false
    this.showTab("model")
    this.loadRegions()
    this.loadMakes()
  }

  // ── Tab management ──────────────────────────────────────────────────

  switchTab(event) {
    const tab = event.currentTarget.dataset.tab
    this.activeTab = tab
    this.showTab(tab)

    // Lazy-load tire section widths on first tab visit
    if (tab === "tire" && !this.tireLoaded) {
      this.tireLoaded = true
      this.loadTireSectionWidths()
    }
  }

  showTab(tabName) {
    this.tabButtonTargets.forEach(btn => {
      const isActive = btn.dataset.tab === tabName
      btn.classList.toggle("bg-secondary", isActive)
      btn.classList.toggle("text-secondary-foreground", isActive)
      btn.classList.toggle("text-muted-foreground", !isActive)
      btn.classList.toggle("hover:text-foreground", !isActive)
      btn.classList.toggle("hover:bg-accent", !isActive)
    })
    this.tabPanelTargets.forEach(panel => {
      panel.classList.toggle("hidden", panel.dataset.tab !== tabName)
    })
  }

  // ── Core API query ──────────────────────────────────────────────────

  async apiQuery(endpoint, params = {}) {
    try {
      const response = await fetch(this.queryUrlValue, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json",
          "X-CSRF-Token": this.csrfTokenValue
        },
        body: JSON.stringify({ endpoint, params })
      })

      const data = await response.json()
      this.updateRawResponse(data)
      return data
    } catch (err) {
      const errorData = {
        success: false,
        error: err.message,
        endpoint,
        params,
        timestamp: new Date().toISOString()
      }
      this.updateRawResponse(errorData)
      return errorData
    }
  }

  // ── Raw response panel ──────────────────────────────────────────────

  updateRawResponse(data) {
    if (this.hasRawEndpointTarget) {
      const paramStr = data.params && Object.keys(data.params).length > 0
        ? `(${JSON.stringify(data.params)})`
        : "()"
      this.rawEndpointTarget.textContent = `${data.endpoint || "--"}${paramStr}`
    }
    if (this.hasRawStatusTarget) {
      const code = data.http_code || (data.success ? 200 : "error")
      this.rawStatusTarget.textContent = code
      this.rawStatusTarget.className = data.success
        ? "font-mono font-semibold text-emerald-600"
        : "font-mono font-semibold text-red-600"
    }
    if (this.hasRawTimestampTarget) {
      this.rawTimestampTarget.textContent = data.timestamp || new Date().toISOString()
    }
    if (this.hasRawBodyTarget) {
      this.rawBodyTarget.textContent = JSON.stringify(data, null, 2)
    }
  }

  toggleRawResponse() {
    if (this.hasRawPanelTarget) {
      this.rawPanelTarget.classList.toggle("hidden")
    }
  }

  // ── Tab 1: Search by Model ─────────────────────────────────────────

  async loadRegions() {
    const data = await this.apiQuery("regions")
    if (data.success && data.data) {
      this.populateSelect(this.regionSelectTarget, data.data, "slug", "name", "-- Wszystkie regiony --")
    }
  }

  async loadMakes() {
    const region = this.hasRegionSelectTarget ? this.regionSelectTarget.value : ""
    this.makeSelectTarget.disabled = true
    const data = await this.apiQuery("makes", { region })
    if (data.success && data.data) {
      this.populateSelect(this.makeSelectTarget, data.data, "slug", "name", "-- Wybierz marke --")
    }
    this.makeSelectTarget.disabled = false
  }

  async regionChanged() {
    this.clearSelect(this.makeSelectTarget, "-- Wybierz marke --")
    this.clearSelect(this.modelSelectTarget, "-- Wybierz model --")
    this.clearSelect(this.yearSelectTarget, "-- Wybierz rok --")
    this.modelResultsTarget.innerHTML = ""
    await this.loadMakes()
  }

  async makeChanged() {
    this.clearSelect(this.modelSelectTarget, "-- Wybierz model --")
    this.clearSelect(this.yearSelectTarget, "-- Wybierz rok --")
    this.modelResultsTarget.innerHTML = ""

    const make = this.makeSelectTarget.value
    if (!make) return

    this.modelSelectTarget.disabled = true
    const data = await this.apiQuery("models", {
      make,
      region: this.regionSelectTarget.value
    })
    if (data.success && data.data) {
      this.populateSelect(this.modelSelectTarget, data.data, "slug", "name", "-- Wybierz model --")
    }
    this.modelSelectTarget.disabled = false
  }

  async modelChanged() {
    this.clearSelect(this.yearSelectTarget, "-- Wybierz rok --")
    this.modelResultsTarget.innerHTML = ""

    const make = this.makeSelectTarget.value
    const model = this.modelSelectTarget.value
    if (!make || !model) return

    this.yearSelectTarget.disabled = true
    const data = await this.apiQuery("years", {
      make,
      model,
      region: this.regionSelectTarget.value
    })
    if (data.success && data.data) {
      this.populateSelect(this.yearSelectTarget, data.data, "slug", "name", "-- Wybierz rok --")
    }
    this.yearSelectTarget.disabled = false
  }

  async yearChanged() {
    this.modelResultsTarget.innerHTML = ""

    const make = this.makeSelectTarget.value
    const model = this.modelSelectTarget.value
    const year = this.yearSelectTarget.value
    if (!make || !model || !year) return

    this.showLoading(this.modelLoadingTarget)
    const data = await this.apiQuery("search_by_model", {
      make,
      model,
      year,
      region: this.regionSelectTarget.value
    })
    this.hideLoading(this.modelLoadingTarget)

    if (data.success && data.data) {
      this.renderModelResults(data.data)
    } else if (data.error) {
      this.modelResultsTarget.innerHTML = this.renderError(data.error)
    }
  }

  // ── Tab 2: Search by Tire ──────────────────────────────────────────

  async loadTireSectionWidths() {
    this.tireSwTarget.disabled = true
    const data = await this.apiQuery("tire_section_widths")
    if (data.success && data.data) {
      const items = data.data.map(v => ({ value: String(v), label: String(v) }))
      this.populateSelectRaw(this.tireSwTarget, items, "-- Szerokosc (mm) --")
    }
    this.tireSwTarget.disabled = false
  }

  async tireSectionWidthChanged() {
    this.clearSelect(this.tireArTarget, "-- Profil --")
    this.clearSelect(this.tireRdTarget, "-- Srednica felgi --")
    this.tireResultsTarget.innerHTML = ""

    const sw = this.tireSwTarget.value
    if (!sw) return

    this.tireArTarget.disabled = true
    const data = await this.apiQuery("tire_aspect_ratios", { section_width: sw })
    if (data.success && data.data) {
      const items = data.data.map(v => ({ value: String(v), label: String(v) }))
      this.populateSelectRaw(this.tireArTarget, items, "-- Profil --")
    }
    this.tireArTarget.disabled = false
  }

  async tireAspectRatioChanged() {
    this.clearSelect(this.tireRdTarget, "-- Srednica felgi --")
    this.tireResultsTarget.innerHTML = ""

    const sw = this.tireSwTarget.value
    const ar = this.tireArTarget.value
    if (!sw || !ar) return

    this.tireRdTarget.disabled = true
    const data = await this.apiQuery("tire_rim_diameters", {
      section_width: sw,
      aspect_ratio: ar
    })
    if (data.success && data.data) {
      const items = data.data.map(v => ({ value: String(v), label: String(v) }))
      this.populateSelectRaw(this.tireRdTarget, items, "-- Srednica felgi --")
    }
    this.tireRdTarget.disabled = false
  }

  async searchByTire() {
    const sw = this.tireSwTarget.value
    const ar = this.tireArTarget.value
    const rd = this.tireRdTarget.value
    if (!sw || !ar || !rd) return

    this.tireResultsTarget.innerHTML = ""
    this.showLoading(this.tireLoadingTarget)
    const data = await this.apiQuery("search_by_tire", {
      section_width: sw,
      aspect_ratio: ar,
      rim_diameter: rd
    })
    this.hideLoading(this.tireLoadingTarget)

    if (data.success && data.data) {
      this.renderVehicleList(this.tireResultsTarget, data.data,
        `Pasujace pojazdy dla opony ${sw}/${ar}R${rd}`)
    } else if (data.error) {
      this.tireResultsTarget.innerHTML = this.renderError(data.error)
    }
  }

  // ── Tab 3: Search by Rim ───────────────────────────────────────────

  async rimBoltPatternChanged() {
    this.clearSelect(this.rimRdTarget, "-- Srednica felgi --")
    this.clearSelect(this.rimRwTarget, "-- Szerokosc felgi --")
    this.rimResultsTarget.innerHTML = ""

    const bp = this.rimBpTarget.value.trim()
    if (!bp) return

    this.rimRdTarget.disabled = true
    const data = await this.apiQuery("rim_diameters", { bolt_pattern: bp })
    if (data.success && data.data) {
      const items = data.data.map(v => ({ value: String(v), label: String(v) }))
      this.populateSelectRaw(this.rimRdTarget, items, "-- Srednica felgi --")
    }
    this.rimRdTarget.disabled = false
  }

  async rimDiameterChanged() {
    this.clearSelect(this.rimRwTarget, "-- Szerokosc felgi --")
    this.rimResultsTarget.innerHTML = ""

    const bp = this.rimBpTarget.value.trim()
    const rd = this.rimRdTarget.value
    if (!bp || !rd) return

    this.rimRwTarget.disabled = true
    const data = await this.apiQuery("rim_widths", {
      bolt_pattern: bp,
      rim_diameter: rd
    })
    if (data.success && data.data) {
      const items = data.data.map(v => ({ value: String(v), label: String(v) }))
      this.populateSelectRaw(this.rimRwTarget, items, "-- Szerokosc felgi --")
    }
    this.rimRwTarget.disabled = false
  }

  async searchByRim() {
    const bp = this.rimBpTarget.value.trim()
    const rd = this.rimRdTarget.value
    const rw = this.rimRwTarget.value
    if (!bp) return

    this.rimResultsTarget.innerHTML = ""
    this.showLoading(this.rimLoadingTarget)
    const data = await this.apiQuery("search_by_rim", {
      bolt_pattern: bp,
      rim_diameter: rd,
      rim_width: rw
    })
    this.hideLoading(this.rimLoadingTarget)

    if (data.success && data.data) {
      this.renderVehicleList(this.rimResultsTarget, data.data,
        `Pasujace pojazdy dla felgi ${bp} ${rd ? rd + '"' : ""} ${rw || ""}`)
    } else if (data.error) {
      this.rimResultsTarget.innerHTML = this.renderError(data.error)
    }
  }

  // ── Select helpers ─────────────────────────────────────────────────

  populateSelect(selectEl, items, valueKey, labelKey, placeholder) {
    selectEl.innerHTML = `<option value="">${this.esc(placeholder)}</option>`
    ;(items || []).forEach(item => {
      const opt = document.createElement("option")
      opt.value = item[valueKey] || ""
      opt.textContent = item[labelKey] || item[valueKey] || ""
      selectEl.appendChild(opt)
    })
  }

  populateSelectRaw(selectEl, items, placeholder) {
    selectEl.innerHTML = `<option value="">${this.esc(placeholder)}</option>`
    ;(items || []).forEach(item => {
      const opt = document.createElement("option")
      opt.value = item.value
      opt.textContent = item.label
      selectEl.appendChild(opt)
    })
  }

  clearSelect(selectEl, placeholder) {
    selectEl.innerHTML = `<option value="">${this.esc(placeholder)}</option>`
  }

  showLoading(target) { if (target) target.classList.remove("hidden") }
  hideLoading(target) { if (target) target.classList.add("hidden") }

  // ── Render: Model fitment results ──────────────────────────────────

  renderModelResults(modifications) {
    if (!modifications || modifications.length === 0) {
      this.modelResultsTarget.innerHTML = `
        <p class="text-sm text-muted-foreground py-4">Brak wynikow dla wybranych parametrow.</p>`
      return
    }

    let html = `
      <p class="text-sm text-muted-foreground mb-3">
        Znaleziono <strong>${modifications.length}</strong> modyfikacji
      </p>`

    modifications.forEach(mod => {
      const trim = mod.trim || ""
      const generation = mod.generation?.name || ""
      const engine = mod.engine?.type || ""
      const power = mod.power ? `${mod.power.PS || "?"} KM` : ""
      const headerParts = [trim, generation, engine, power].filter(Boolean)

      html += `
        <div class="mb-4 rounded-lg border bg-card">
          <div class="border-b bg-muted/30 px-4 py-2">
            <span class="text-sm font-semibold">${this.esc(headerParts.join(" / "))}</span>
          </div>`

      const wheels = mod.wheels || []
      if (wheels.length > 0) {
        html += this.renderWheelGroups(wheels)
      }

      html += `</div>`
    })

    this.modelResultsTarget.innerHTML = html
  }

  // ── Render: Vehicle list (tire / rim search) ───────────────────────

  renderVehicleList(target, vehicles, title) {
    if (!vehicles || vehicles.length === 0) {
      target.innerHTML = `
        <p class="text-sm text-muted-foreground py-4">Brak pasujacych pojazdow.</p>`
      return
    }

    let html = `
      <p class="text-sm text-muted-foreground mb-3">
        ${this.esc(title)}: <strong>${vehicles.length}</strong> wynikow
      </p>
      <div class="overflow-x-auto rounded-lg border">
        <table class="w-full text-sm">
          <thead>
            <tr class="border-b bg-muted/30">
              <th class="px-4 py-2 text-left font-medium text-muted-foreground">#</th>
              <th class="px-4 py-2 text-left font-medium text-muted-foreground">Marka</th>
              <th class="px-4 py-2 text-left font-medium text-muted-foreground">Model</th>
              <th class="px-4 py-2 text-left font-medium text-muted-foreground">Roczniki</th>
            </tr>
          </thead>
          <tbody>`

    vehicles.forEach((v, i) => {
      const makeName = v.make?.name || v.make || "--"
      const modelName = v.model?.name || v.model || "--"
      const years = v.years || []
      const yearStr = years.length > 0 ? `${years[0]}–${years[years.length - 1]}` : "--"

      html += `
            <tr class="border-b hover:bg-muted/20">
              <td class="px-4 py-2 text-muted-foreground">${i + 1}</td>
              <td class="px-4 py-2 font-medium">${this.esc(makeName)}</td>
              <td class="px-4 py-2">${this.esc(modelName)}</td>
              <td class="px-4 py-2 font-mono text-xs">${this.esc(yearStr)}</td>
            </tr>`
    })

    html += `
          </tbody>
        </table>
      </div>`

    target.innerHTML = html
  }

  // ── Render: Error ──────────────────────────────────────────────────

  renderError(message) {
    return `
      <div class="rounded-md border border-destructive/25 bg-destructive/10 px-4 py-3 text-sm text-destructive">
        ${this.esc(message)}
      </div>`
  }

  // ── Utilities ──────────────────────────────────────────────────────

  renderWheelGroups(wheels) {
    const groups = wheels.reduce((acc, wheel) => {
      const diameterLabel = this.wheelDiameterLabel(wheel)
      if (!acc[diameterLabel]) acc[diameterLabel] = []
      acc[diameterLabel].push(wheel)
      return acc
    }, {})

    const orderedGroups = Object.entries(groups).sort((a, b) => {
      return this.wheelDiameterValue(b[0]) - this.wheelDiameterValue(a[0])
    })

    let html = `
      <div class="px-4 py-4">
        <div class="mb-4">
          <p class="text-sm font-semibold text-foreground">Rozmiary opon</p>
        </div>
        <div class="space-y-4">`

    orderedGroups.forEach(([diameter, groupWheels]) => {
      const orderedWheels = [...groupWheels].sort((a, b) => Number(b.is_stock) - Number(a.is_stock))

      html += `
        <div class="rounded-xl border border-border/70 bg-muted/10">
          <div class="border-b border-border/70 px-4 py-2">
            <p class="text-sm font-semibold text-foreground">${this.esc(diameter)}</p>
          </div>
          <div class="space-y-3 p-4">`

      orderedWheels.forEach((wheel, index) => {
        html += `
          <div class="rounded-lg border border-border/60 bg-background px-4 py-3">
            <p class="mb-3 text-xs font-medium uppercase tracking-wide text-muted-foreground">
              Konfiguracja ${index + 1}
            </p>
            <div class="grid gap-3 md:grid-cols-2">
              ${this.renderAxleDetails("Przod", wheel.front)}
              ${this.renderAxleDetails("Tyl", wheel.rear)}
            </div>
          </div>`
      })

      html += `
          </div>
        </div>`
    })

    html += `
        </div>
      </div>`

    return html
  }

  renderAxleDetails(label, axle) {
    const tire = axle?.tire || "Brak danych"
    const rim = axle?.rim || "Brak danych"
    const pressure = axle?.tire_pressure?.bar

    return `
      <div class="rounded-lg bg-muted/20 px-3 py-3">
        <p class="mb-2 text-sm font-semibold text-foreground">${this.esc(label)}</p>
        <dl class="space-y-2 text-xs">
          <div>
            <dt class="mb-0.5 text-muted-foreground">Opona</dt>
            <dd class="font-mono text-foreground">${this.esc(tire)}</dd>
          </div>
          <div>
            <dt class="mb-0.5 text-muted-foreground">Felga</dt>
            <dd class="font-mono text-foreground">${this.esc(rim)}</dd>
          </div>
          <div>
            <dt class="mb-0.5 text-muted-foreground">Cisnienie</dt>
            <dd class="font-mono text-foreground">${this.esc(pressure ? `${pressure} bar` : "Brak danych")}</dd>
          </div>
        </dl>
      </div>`
  }

  wheelDiameterLabel(wheel) {
    const diameter = this.extractWheelDiameter(
      wheel?.front?.tire ||
      wheel?.rear?.tire ||
      wheel?.front?.rim ||
      wheel?.rear?.rim ||
      ""
    )

    return diameter ? `${diameter}"` : "Inne"
  }

  wheelDiameterValue(label) {
    const match = String(label).match(/(\d+(?:[.,]\d+)?)/)
    return match ? Number(match[1].replace(",", ".")) : -1
  }

  extractWheelDiameter(value) {
    const normalized = String(value || "")
    const tireMatch = normalized.match(/R(\d+(?:[.,]\d+)?)/i)
    if (tireMatch) return tireMatch[1].replace(",", ".")

    const rimMatch = normalized.match(/x(\d+(?:[.,]\d+)?)/i)
    if (rimMatch) return rimMatch[1].replace(",", ".")

    return null
  }

  esc(str) {
    if (str === null || str === undefined) return ""
    const div = document.createElement("div")
    div.textContent = String(str)
    return div.innerHTML
  }
}
